<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import BusinessDay from '../../../inbox/components/BusinessDay.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
} from '../../../inbox/helpers/businessHour';

export default {
  components: {
    SettingsSection,
    BusinessDay,
    NextButton,
    NextSelect,
  },
  props: {
    team: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedFirstInteractionDeadlineMinutes: 5,
      firstInteractionDeadlineMinutesOptions: [
        { label: '5 minutos', value: 5 },
        { label: '10 minutos', value: 10 },
        { label: '15 minutos', value: 15 },
        { label: '20 minutos', value: 20 },
        { label: '25 minutos', value: 25 },
        { label: '30 minutos', value: 30 },
        { label: '35 minutos', value: 35 },
        { label: '40 minutos', value: 40 },
        { label: '45 minutos', value: 45 },
        { label: '50 minutos', value: 50 },
        { label: '55 minutos', value: 55 },
        { label: '60 minutos', value: 60 },
        { label: '75 minutos', value: 75 },
        { label: '90 minutos', value: 90 },
        { label: '120 minutos', value: 120 },
        { label: '24 horas', value: 1440 },
      ],
      isRedistributionEnabled: false,
      dayNames: {
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday',
      },
      timeSlots: [...defaultTimeSlot],
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    hasError() {
      if (!this.isRedistributionEnabled) return false;
      return this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
    },
  },
  watch: {
    team() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        redistribution_enabled: isRedistributionEnabled = false,
        first_interaction_deadline_minutes: firstInteractionDeadlineMinutes = 5,
        working_hours: timeSlots = [],
      } = this.team;

      this.isRedistributionEnabled = isRedistributionEnabled;

      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;

      this.selectedFirstInteractionDeadlineMinutes =
        firstInteractionDeadlineMinutes;
      this.timeSlots = slots;
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    async updateTeam() {
      try {
        const payload = {
          id: this.team.id,
          redistribution_enabled: this.isRedistributionEnabled,
          first_interaction_deadline_minutes:
            this.selectedFirstInteractionDeadlineMinutes,
          working_hours: timeSlotTransform(this.timeSlots),
        };
        await this.$store.dispatch('teams/update', payload);
        useAlert(
          'Configurações de redistribuição de leads do time atualizadas com sucesso'
        );
      } catch (error) {
        useAlert(
          error.message ||
            'Não foi possível atualizar as configurações de redistribuição de leads do time. Por favor, tente novamente mais tarde.'
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.TITLE')"
      :sub-title="$t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.DESCRIPTION')"
    >
      <form @submit.prevent="updateTeam">
        <label for="toggle-business-hours" class="toggle-input-wrap">
          <input
            v-model="isRedistributionEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-business-hours"
          />
          {{ $t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.TOGGLE') }}
        </label>
        <p class="text-slate-700 dark:text-slate-300 mb-4">
          {{ $t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.TOGGLE_HELP') }}
        </p>
        <div v-if="isRedistributionEnabled" class="mb-6">
          <div class="max-w-[37.5rem]">
            <label>
              {{ $t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.DEADLINE_LABEL') }}
            </label>
            <!-- Zimobi: vue-multiselect left with Chatwoot 4.11; the deadline is a plain select. -->
            <NextSelect
              v-model="selectedFirstInteractionDeadlineMinutes"
              :options="firstInteractionDeadlineMinutesOptions"
              class="mt-2"
            />
            <p class="text-slate-700 dark:text-slate-300 mb-4">
              {{ $t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.DEADLINE_HELP') }}
            </p>
          </div>
          <label>
            {{ $t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.WEEKLY_HOURS') }}
          </label>
          <!-- Zimobi: BusinessDay is a table row since Chatwoot 4.11, as in WeeklyAvailability. -->
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl mt-2"
          >
            <tbody class="divide-y divide-n-weak">
              <BusinessDay
                v-for="timeSlot in timeSlots"
                :key="timeSlot.day"
                :day-name="dayNames[timeSlot.day]"
                :time-slot="timeSlot"
                @update="data => onSlotUpdate(timeSlot.day, data)"
              />
            </tbody>
          </table>
        </div>
        <NextButton
          type="submit"
          :label="$t('TEAMS_SETTINGS.LEAD_REDISTRIBUTION.SUBMIT')"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </form>
    </SettingsSection>
  </div>
</template>
